import { useEffect, useState, useRef } from "react";
import { motion, useInView } from "framer-motion";

const languages = [
  {
    name: "pipeline.rs",
    lang: "Rust",
    color: "#DEA584",
    lines: [
      { text: "pub async fn execute_trade(", color: "keyword" },
      { text: "    order: &Order) -> Result<Fill> {", color: "default" },
      { text: "  let venue = router::select_venue(", color: "default" },
      { text: "    order, &self.venues)?;", color: "string" },
      { text: "  let risk = risk_engine::check(", color: "default" },
      { text: "    order, &self.limits).await?;", color: "string" },
      { text: "", color: "default" },
      { text: '  metrics::record_latency(fill.time);', color: "number" },
      { text: "  Ok(fill)", color: "number" },
      { text: "}", color: "default" },
    ],
  },
  {
    name: "engine.py",
    lang: "Python",
    color: "#3572A5",
    lines: [
      { text: "class RAGEngine:", color: "keyword" },
      { text: "  async def query(self, prompt):", color: "keyword" },
      { text: '    emb = await self.embed(prompt)', color: "default" },
      { text: "    chunks = self.vector_db.search(", color: "default" },
      { text: "      emb, top_k=5, threshold=0.82)", color: "number" },
      { text: "", color: "default" },
      { text: "    context = self.build_context(chunks)", color: "default" },
      { text: "    response = await self.llm.generate(", color: "default" },
      { text: '      prompt=prompt, context=context,', color: "string" },
      { text: "      temperature=0.1)", color: "number" },
    ],
  },
  {
    name: "Service.java",
    lang: "Java",
    color: "#B07219",
    lines: [
      { text: "public class PaymentOrchestrator {", color: "keyword" },
      { text: "  public CompletableFuture<Receipt>", color: "keyword" },
      { text: "    process(PaymentRequest req) {", color: "default" },
      { text: "", color: "default" },
      { text: "    var provider = routingEngine", color: "default" },
      { text: "      .selectOptimal(req.region(),", color: "string" },
      { text: "        req.amount());", color: "string" },
      { text: "", color: "default" },
      { text: "    return provider.charge(req)", color: "default" },
      { text: "      .thenApply(this::reconcile);", color: "number" },
    ],
  },
  {
    name: "deploy.ts",
    lang: "TypeScript",
    color: "#3178C6",
    lines: [
      { text: "async function deployPipeline(", color: "keyword" },
      { text: "  config: Config) {", color: "default" },
      { text: "  const cluster = await k8s.connect({", color: "default" },
      { text: '    region: config.region,', color: "string" },
      { text: "    replicas: config.scale,", color: "number" },
      { text: "  });", color: "default" },
      { text: "", color: "default" },
      { text: "  const pipeline = new MLPipeline({", color: "default" },
      { text: '    model: "rag-v3-financial",', color: "string" },
      { text: "    temperature: 0.1 });", color: "number" },
    ],
  },
];

const colorMap = {
  keyword: "text-[#c792ea]",
  string: "text-[#c3e88d]",
  number: "text-[#f78c6c]",
  default: "text-slate-300",
};

/* Individual language card for the 2x2 grid */
const LanguageCard = ({ lang, index }) => {
  const [visibleLines, setVisibleLines] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-30px" });

  useEffect(() => {
    if (!isInView) return;
    setVisibleLines(0);
    const timer = setInterval(() => {
      setVisibleLines((prev) => {
        if (prev >= lang.lines.length) {
          clearInterval(timer);
          return prev;
        }
        return prev + 1;
      });
    }, 100);
    return () => clearInterval(timer);
  }, [isInView, lang.lines.length]);

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 20 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.5, delay: index * 0.15 }}
      data-testid={`code-card-${lang.lang.toLowerCase()}`}
      className="rounded-xl overflow-hidden border border-white/10 bg-[#1a1e2e]/80 backdrop-blur-sm h-full flex flex-col"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-2.5 bg-[#1a1e2e] border-b border-white/5">
        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: lang.color }} />
        <span className="font-mono text-xs text-slate-400">{lang.name}</span>
        <span className="ml-auto font-mono text-[10px] text-slate-600 uppercase tracking-wider">{lang.lang}</span>
      </div>

      {/* Code */}
      <div className="p-4 font-mono text-[11px] leading-[1.7] flex-1">
        {lang.lines.map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, x: -8 }}
            animate={i < visibleLines ? { opacity: 1, x: 0 } : { opacity: 0, x: -8 }}
            transition={{ duration: 0.15 }}
            className="flex"
          >
            <span className="text-slate-700 select-none w-5 text-right mr-3 flex-shrink-0 text-[10px]">
              {i + 1}
            </span>
            <span className={colorMap[line.color] || "text-slate-300"}>
              {line.text || "\u00A0"}
            </span>
          </motion.div>
        ))}
      </div>
    </motion.div>
  );
};

/* 2x2 Code Grid for Hero */
export const CodeShowcase = ({ className = "" }) => {
  return (
    <div className={`grid grid-cols-2 gap-3 ${className}`}>
      {languages.map((lang, i) => (
        <LanguageCard key={lang.lang} lang={lang} index={i} />
      ))}
    </div>
  );
};

/* Full tabbed code snippet - accepts variant prop for different content */
export const CodeSnippet = ({ className = "", variant = "hero" }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [visibleLines, setVisibleLines] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  const heroLanguages = [
    {
      name: "pipeline.rs",
      lang: "Rust",
      color: "#DEA584",
      lines: [
        { indent: 0, text: "pub async fn execute_trade(order: &Order) -> Result<Fill> {", color: "keyword" },
        { indent: 1, text: "let venue = router::select_venue(order, &self.venues)?;", color: "default" },
        { indent: 1, text: 'let risk = risk_engine::check(order, &self.limits).await?;', color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 1, text: "if risk.approved {", color: "keyword" },
        { indent: 2, text: "let fill = venue.submit(order).await?;", color: "default" },
        { indent: 2, text: "metrics::record_latency(fill.execution_time);", color: "string" },
        { indent: 2, text: "ledger::update_position(&fill).await?;", color: "string" },
        { indent: 2, text: "Ok(fill)", color: "number" },
        { indent: 1, text: "} else {", color: "keyword" },
        { indent: 2, text: "Err(RiskError::LimitBreached(risk.reason))", color: "number" },
        { indent: 1, text: "}", color: "default" },
        { indent: 0, text: "}", color: "default" },
      ],
    },
    {
      name: "engine.py",
      lang: "Python",
      color: "#3572A5",
      lines: [
        { indent: 0, text: "class RAGEngine:", color: "keyword" },
        { indent: 1, text: "async def query(self, prompt: str) -> Response:", color: "keyword" },
        { indent: 2, text: 'embeddings = await self.embed(prompt, model="v3")', color: "default" },
        { indent: 2, text: "chunks = self.vector_db.search(", color: "default" },
        { indent: 3, text: "embeddings, top_k=5, threshold=0.82", color: "number" },
        { indent: 2, text: ")", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "context = self.build_context(chunks)", color: "default" },
        { indent: 2, text: "response = await self.llm.generate(", color: "default" },
        { indent: 3, text: "prompt=prompt, context=context,", color: "string" },
        { indent: 3, text: "temperature=0.1, max_tokens=2048", color: "number" },
        { indent: 2, text: ")", color: "default" },
        { indent: 2, text: "return self.validate(response)", color: "default" },
      ],
    },
    {
      name: "Service.java",
      lang: "Java",
      color: "#B07219",
      lines: [
        { indent: 0, text: "public class PaymentOrchestrator {", color: "keyword" },
        { indent: 1, text: "public CompletableFuture<Receipt> process(", color: "keyword" },
        { indent: 3, text: "PaymentRequest request) {", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "var provider = routingEngine", color: "default" },
        { indent: 3, text: ".selectOptimal(request.region(),", color: "string" },
        { indent: 4, text: "request.amount());", color: "string" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "return provider.charge(request)", color: "default" },
        { indent: 3, text: ".thenApply(this::reconcile)", color: "default" },
        { indent: 3, text: ".exceptionally(e ->", color: "keyword" },
        { indent: 4, text: "fallbackProvider.charge(request));", color: "number" },
        { indent: 1, text: "}", color: "default" },
        { indent: 0, text: "}", color: "default" },
      ],
    },
    {
      name: "deploy.ts",
      lang: "TypeScript",
      color: "#3178C6",
      lines: [
        { indent: 0, text: "async function deployPipeline(config: Config) {", color: "keyword" },
        { indent: 1, text: "const cluster = await k8s.connect({", color: "default" },
        { indent: 2, text: "region: config.region,", color: "string" },
        { indent: 2, text: "replicas: config.scale,", color: "number" },
        { indent: 1, text: "});", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 1, text: "const pipeline = new MLPipeline({", color: "default" },
        { indent: 2, text: 'model: "rag-v3-financial",', color: "string" },
        { indent: 2, text: "embeddings: 1536,", color: "number" },
        { indent: 2, text: "temperature: 0.1,", color: "number" },
        { indent: 1, text: "});", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 1, text: "await pipeline.validate();", color: "default" },
        { indent: 1, text: "await cluster.deploy(pipeline);", color: "default" },
        { indent: 1, text: 'return { status: "live", latency: "<2ms" };', color: "string" },
        { indent: 0, text: "}", color: "default" },
      ],
    },
  ];

  const engLanguages = [
    {
      name: "stream.rs",
      lang: "Rust",
      color: "#DEA584",
      lines: [
        { indent: 0, text: "pub struct DataPipeline<T: Serialize> {", color: "keyword" },
        { indent: 1, text: "buffer: Arc<RwLock<VecDeque<T>>>,", color: "default" },
        { indent: 1, text: "capacity: usize,", color: "number" },
        { indent: 0, text: "}", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 0, text: "impl<T: Serialize> DataPipeline<T> {", color: "keyword" },
        { indent: 1, text: "pub async fn ingest(&self, batch: Vec<T>) {", color: "keyword" },
        { indent: 2, text: "let mut buf = self.buffer.write().await;", color: "default" },
        { indent: 2, text: "for item in batch {", color: "keyword" },
        { indent: 3, text: "buf.push_back(item);", color: "default" },
        { indent: 3, text: "if buf.len() > self.capacity {", color: "keyword" },
        { indent: 4, text: "self.flush(&mut buf).await;", color: "string" },
        { indent: 3, text: "}", color: "default" },
        { indent: 2, text: "}", color: "default" },
        { indent: 1, text: "}", color: "default" },
        { indent: 0, text: "}", color: "default" },
      ],
    },
    {
      name: "train.py",
      lang: "Python",
      color: "#3572A5",
      lines: [
        { indent: 0, text: "class ModelTrainer:", color: "keyword" },
        { indent: 1, text: "def fit(self, dataset: Dataset) -> Metrics:", color: "keyword" },
        { indent: 2, text: "loader = DataLoader(dataset, batch=64)", color: "default" },
        { indent: 2, text: "for epoch in range(self.epochs):", color: "keyword" },
        { indent: 3, text: "for batch in loader:", color: "keyword" },
        { indent: 4, text: "loss = self.model(batch)", color: "default" },
        { indent: 4, text: "loss.backward()", color: "string" },
        { indent: 4, text: "self.optimizer.step()", color: "string" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "metrics = self.evaluate(dataset.val)", color: "default" },
        { indent: 2, text: 'logger.info(f"epoch={epoch} f1={metrics.f1:.3f}")', color: "number" },
        { indent: 2, text: "return metrics", color: "default" },
      ],
    },
    {
      name: "Event.java",
      lang: "Java",
      color: "#B07219",
      lines: [
        { indent: 0, text: "@EventHandler", color: "string" },
        { indent: 0, text: "public class TransactionProcessor {", color: "keyword" },
        { indent: 1, text: "public Mono<Result> handle(TxEvent event) {", color: "keyword" },
        { indent: 2, text: "return Mono.just(event)", color: "default" },
        { indent: 3, text: ".flatMap(this::validate)", color: "default" },
        { indent: 3, text: ".flatMap(this::enrich)", color: "string" },
        { indent: 3, text: ".flatMap(this::persist)", color: "string" },
        { indent: 3, text: ".doOnSuccess(r ->", color: "keyword" },
        { indent: 4, text: 'metrics.counter("tx.processed").inc())', color: "number" },
        { indent: 3, text: ".onErrorResume(e ->", color: "keyword" },
        { indent: 4, text: "deadLetter.publish(event, e));", color: "number" },
        { indent: 1, text: "}", color: "default" },
        { indent: 0, text: "}", color: "default" },
      ],
    },
    {
      name: "test.ts",
      lang: "TypeScript",
      color: "#3178C6",
      lines: [
        { indent: 0, text: 'describe("PaymentService", () => {', color: "keyword" },
        { indent: 1, text: 'it("processes concurrent payments", async () => {', color: "keyword" },
        { indent: 2, text: "const payments = Array.from({ length: 100 },", color: "default" },
        { indent: 3, text: "() => generatePayment());", color: "default" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "const results = await Promise.all(", color: "default" },
        { indent: 3, text: "payments.map(p => service.process(p)));", color: "string" },
        { indent: 0, text: "", color: "default" },
        { indent: 2, text: "const failures = results.filter(r => !r.ok);", color: "default" },
        { indent: 2, text: "expect(failures.length).toBe(0);", color: "number" },
        { indent: 2, text: "expect(metrics.p99Latency).toBeLessThan(50);", color: "number" },
        { indent: 1, text: "});", color: "default" },
        { indent: 0, text: "});", color: "default" },
      ],
    },
  ];

  const fullLanguages = variant === "engineering" ? engLanguages : heroLanguages;

  const currentLang = fullLanguages[activeTab];

  useEffect(() => {
    if (!isInView) return;
    setVisibleLines(0);
    const timer = setInterval(() => {
      setVisibleLines((prev) => {
        if (prev >= currentLang.lines.length) { clearInterval(timer); return prev; }
        return prev + 1;
      });
    }, 80);
    return () => clearInterval(timer);
  }, [isInView, activeTab, currentLang.lines.length]);

  return (
    <div ref={ref} className={`rounded-xl overflow-hidden border border-white/10 bg-[#1a1e2e]/80 backdrop-blur-sm ${className}`}>
      <div className="flex items-center bg-[#1a1e2e] border-b border-white/5">
        <div className="flex items-center gap-1.5 px-3 py-3 flex-shrink-0">
          <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f56]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#ffbd2e]" />
          <span className="w-2.5 h-2.5 rounded-full bg-[#27c93f]" />
        </div>
        <div className="flex flex-1 min-w-0">
          {fullLanguages.map((lang, i) => (
            <button
              key={lang.name}
              onClick={() => { setActiveTab(i); setVisibleLines(0); }}
              data-testid={`code-tab-${lang.lang.toLowerCase()}`}
              className={`px-2.5 py-3 font-mono text-[11px] transition-colors duration-200 border-b-2 whitespace-nowrap ${
                activeTab === i
                  ? "text-white border-teal-bright bg-white/5"
                  : "text-slate-300 border-transparent hover:text-white"
              }`}
            >
              <span className="inline-block w-2 h-2 rounded-full mr-1 align-middle" style={{ backgroundColor: lang.color }} />
              {lang.name}
            </button>
          ))}
        </div>
      </div>
      <div className="p-5 font-mono text-sm leading-7 min-h-[340px]">
        {currentLang.lines.map((line, i) => (
          <motion.div
            key={`${activeTab}-${i}`}
            initial={{ opacity: 0, x: -10 }}
            animate={i < visibleLines ? { opacity: 1, x: 0 } : { opacity: 0, x: -10 }}
            transition={{ duration: 0.2, ease: "easeOut" }}
            className="flex"
          >
            <span className="text-slate-700 select-none w-8 text-right mr-4 flex-shrink-0 text-xs leading-7">
              {i + 1}
            </span>
            <span className={colorMap[line.color] || "text-slate-300"}>
              {"\u00A0".repeat(line.indent * 2)}{line.text}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

const terminalLines = [
  { text: "$ venury deploy --env production", color: "text-teal-bright", delay: 0 },
  { text: "  Validating configuration...", color: "text-slate-500", delay: 0.3 },
  { text: "  Running 847 tests... All passed.", color: "text-emerald-400", delay: 0.8 },
  { text: "  Building containers (3/3)... Done.", color: "text-slate-400", delay: 1.2 },
  { text: "  Deploying to us-east-1... Live.", color: "text-emerald-400", delay: 1.6 },
  { text: "", color: "", delay: 2.0 },
  { text: "  Latency: p50=2ms  p99=8ms", color: "text-orange-400", delay: 2.2 },
  { text: "  Status: Healthy (4/4 replicas)", color: "text-emerald-400", delay: 2.5 },
  { text: "  Dashboard: https://dash.venury.tech", color: "text-teal-bright", delay: 2.8 },
];

export const TerminalSnippet = ({ className = "" }) => {
  const [visibleLines, setVisibleLines] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  useEffect(() => {
    if (!isInView) return;
    setVisibleLines(0);
    const timer = setInterval(() => {
      setVisibleLines((prev) => {
        if (prev >= terminalLines.length) { clearInterval(timer); return prev; }
        return prev + 1;
      });
    }, 300);
    return () => clearInterval(timer);
  }, [isInView]);

  return (
    <div ref={ref} className={`rounded-xl overflow-hidden border border-white/10 bg-[#0d1117]/90 backdrop-blur-sm ${className}`}>
      <div className="flex items-center gap-2 px-4 py-3 bg-[#0d1117] border-b border-white/5">
        <span className="w-3 h-3 rounded-full bg-[#ff5f56]" />
        <span className="w-3 h-3 rounded-full bg-[#ffbd2e]" />
        <span className="w-3 h-3 rounded-full bg-[#27c93f]" />
        <span className="font-mono text-xs text-slate-600 ml-2">terminal</span>
      </div>
      <div className="p-5 font-mono text-sm leading-7 min-h-[340px]">
        {terminalLines.map((line, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0 }}
            animate={i < visibleLines ? { opacity: 1 } : { opacity: 0 }}
            transition={{ duration: 0.3 }}
            className={line.color}
          >
            {line.text}
          </motion.div>
        ))}
        {visibleLines >= terminalLines.length && (
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: [1, 0] }}
            transition={{ duration: 0.8, repeat: Infinity }}
            className="text-teal-bright"
          >
            _
          </motion.span>
        )}
      </div>
    </div>
  );
};
